// Odontograma interactivo (033) — grilla FDI (permanente + temporal en el
// mismo gráfico), un hallazgo por pieza + Recesión/Movilidad independientes.
// Tratamiento visual "premium" (profundidad, glow, transición) autorizado
// por Sue puntualmente para esta pantalla (PLAN.md sección "033") — mismo
// criterio ya usado en los widgets de KPI del dashboard: el resto de la app
// se mantiene en la paleta clínica `hce-*`. El diagrama del PDF firmado
// (`dibujarOdontograma`, `@logiacare/hce-pdf`) usa los MISMOS colores rojo/
// azul de la leyenda oficial, solo que en su versión vectorial sobria — no
// es una fuente de verdad visual distinta, es el mismo dato con menos pulido.
import { type JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CATALOGO_HALLAZGOS_ODONTOGRAMA,
  FILA_PERMANENTE_INFERIOR,
  FILA_PERMANENTE_SUPERIOR,
  FILA_TEMPORAL_INFERIOR,
  FILA_TEMPORAL_SUPERIOR,
  SUPERFICIES_DENTALES,
  contornoDePieza,
  esTemporal,
  hallazgoPintaTodaLaPieza,
  poligonoDeSuperficie,
  simboloBajoPieza,
  simboloOficialDeHallazgo,
  trazosDeSimbolo,
  type CodigoHallazgoOdontograma,
  type EstadoSuperficie,
  type Punto,
  type SuperficieDental,
  type SuperficiesPieza,
} from '@logiacare/hce-pdf';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { EntradaOdontogramaUi, EstadoOdontograma } from './tipos';

const CODIGOS_HALLAZGO = Object.keys(CATALOGO_HALLAZGOS_ODONTOGRAMA) as CodigoHallazgoOdontograma[];

/** Mismos rojo/azul que `dibujarOdontograma` en `@logiacare/hce-pdf` (0xb53535 / 0x2b4c7a) — pantalla y PDF cuentan la misma historia de color, solo cambia el nivel de pulido visual. */
const ESTILO_POR_CATEGORIA: Record<'rojo' | 'azul' | 'neutro', { fondo?: string; glow?: string; texto: string }> = {
  rojo: {
    fondo: 'linear-gradient(155deg, #d9685f 0%, #b53535 60%, #862323 100%)',
    glow: '0 3px 10px -3px rgba(181,53,53,0.65), 0 1px 2px rgba(0,0,0,0.15)',
    texto: '#fff8f6',
  },
  azul: {
    fondo: 'linear-gradient(155deg, #5478a5 0%, #2b4c7a 60%, #1a3355 100%)',
    glow: '0 3px 10px -3px rgba(43,76,122,0.6), 0 1px 2px rgba(0,0,0,0.15)',
    texto: '#f5f8fc',
  },
  neutro: { texto: 'var(--color-hce-muted)' },
};

const COLOR_SUPERFICIE: Record<EstadoSuperficie, string> = { caries: '#b53535', obturado: '#2b4c7a' };
const COLOR_SIMBOLO = { rojo: '#b53535', azul: '#2b4c7a', neutro: '#475569' } as const;
/** Hallazgos que se marcan por superficie (no se ofrecen como "pieza completa" en el selector, salvo que el dato viejo ya los traiga). */
const HALLAZGOS_POR_SUPERFICIE = new Set<CodigoHallazgoOdontograma>(['caries', 'obturado']);

function trazoPath(puntos: readonly Punto[]): string {
  return `${puntos.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`).join(' ')}Z`;
}

/** Estado efectivo de cada superficie: el dato por superficie manda; sin él, caries/obturado de pieza completa (dato viejo) pinta todas. */
function superficiesEfectivas(entrada: EntradaOdontogramaUi): SuperficiesPieza {
  if (entrada.superficies && Object.keys(entrada.superficies).length > 0) return entrada.superficies;
  const toda = hallazgoPintaTodaLaPieza(entrada.hallazgo);
  return toda ? Object.fromEntries(SUPERFICIES_DENTALES.map((s) => [s, toda])) : {};
}

/** Pieza dibujada con sus 5 superficies y el símbolo oficial del hallazgo (mismos polígonos y trazos que el PDF firmado). */
function PiezaSvg({ pieza, entrada, tamano }: { pieza: string; entrada: EntradaOdontogramaUi; tamano: number }): JSX.Element {
  const hallazgo = entrada.hallazgo && entrada.hallazgo !== 'sano' ? entrada.hallazgo : undefined;
  const simbolo = simboloOficialDeHallazgo(hallazgo);
  const color = COLOR_SIMBOLO[categoriaDe(hallazgo)];
  const estados = superficiesEfectivas(entrada);
  const bajo = simbolo !== undefined && simboloBajoPieza(simbolo);
  return (
    <svg width={tamano} height={tamano * (bajo ? 1.25 : 1)} viewBox={`-1.15 -1.15 2.3 ${bajo ? 2.9 : 2.3}`} aria-hidden="true">
      {SUPERFICIES_DENTALES.map((sup) => {
        const estado = estados[sup];
        return <path key={sup} d={trazoPath(poligonoDeSuperficie(pieza, sup))} fill={estado ? COLOR_SUPERFICIE[estado] : 'var(--color-hce-surface)'} stroke="#64748b" strokeWidth={0.06} />;
      })}
      <path d={trazoPath(contornoDePieza(esTemporal(pieza)))} fill="none" stroke="#334155" strokeWidth={0.09} />
      {simbolo && (
        <g transform={bajo ? 'translate(0 1.55) scale(0.4)' : 'scale(0.95)'} stroke={color} fill="none" strokeWidth={bajo ? 0.14 : 0.11} strokeLinecap="round">
          {trazosDeSimbolo(simbolo).map((tr, i) => {
            if (tr.t === 'linea') return <line key={i} x1={tr.a[0]} y1={tr.a[1]} x2={tr.b[0]} y2={tr.b[1]} strokeDasharray={tr.punteada ? '0.12 0.18' : undefined} />;
            if (tr.t === 'poli') return <path key={i} d={trazoPath(tr.pts)} />;
            if (tr.t === 'circulo') return <circle key={i} r={tr.r} />;
            return (
              <text key={i} x={0} y={0.4} textAnchor="middle" fontSize={1.3} fill={color} stroke="none" fontWeight={600}>
                {tr.s}
              </text>
            );
          })}
        </g>
      )}
    </svg>
  );
}

/**
 * Cambia una superficie de una entrada. Un dato viejo de pieza completa (caries/obturado sin superficies) se expande primero
 * a sus 5 superficies y el `hallazgo` viejo se descarta, para que pantalla, CPO y PDF sigan diciendo lo mismo.
 */
function conSuperficie(entrada: EntradaOdontogramaUi, sup: SuperficieDental, valor: string): EntradaOdontogramaUi {
  const siguiente: Partial<Record<SuperficieDental, EstadoSuperficie>> = { ...superficiesEfectivas(entrada) };
  if (valor === 'caries' || valor === 'obturado') siguiente[sup] = valor;
  else delete siguiente[sup];
  const hallazgo = entrada.hallazgo && HALLAZGOS_POR_SUPERFICIE.has(entrada.hallazgo) ? undefined : entrada.hallazgo;
  return { ...entrada, hallazgo, superficies: Object.keys(siguiente).length > 0 ? siguiente : undefined };
}

const HALLAZGOS_DE_PERDIDA = new Set<CodigoHallazgoOdontograma>(['ausente', 'perdida-otra-causa', 'perdida-caries']);

function categoriaDe(hallazgo: CodigoHallazgoOdontograma | undefined): 'rojo' | 'azul' | 'neutro' {
  if (!hallazgo) return 'neutro';
  return CATALOGO_HALLAZGOS_ODONTOGRAMA[hallazgo].color;
}

interface DienteProps {
  readonly pieza: string;
  readonly entrada: EntradaOdontogramaUi;
  readonly onChange: (entrada: EntradaOdontogramaUi) => void;
}

function Diente({ pieza, entrada, onChange }: DienteProps): JSX.Element {
  const { t } = useTranslation();
  const [abierto, setAbierto] = useState(false);
  const hallazgo = entrada.hallazgo && entrada.hallazgo !== 'sano' ? entrada.hallazgo : undefined;
  const tieneDetalle = Boolean(entrada.recesion ?? entrada.movilidad);
  const temporal = esTemporal(pieza);
  const etiquetaHallazgo = hallazgo ? t(`formularios.f033.odontograma.hallazgos.${hallazgo}`) : undefined;

  return (
    <Popover open={abierto} onOpenChange={setAbierto}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${t('formularios.f033.odontograma.pieza')} ${pieza}${etiquetaHallazgo ? ` — ${etiquetaHallazgo}` : ''}`}
          className="group flex flex-col items-center gap-0.5 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-hce-pine/70"
        >
          <span
            className="flex items-center justify-center rounded-md transition-transform duration-200 ease-out group-hover:-translate-y-0.5"
            style={{ boxShadow: abierto ? '0 0 0 2px var(--color-hce-pine)' : undefined }}
          >
            <PiezaSvg pieza={pieza} entrada={entrada} tamano={temporal ? 26 : 32} />
          </span>
          <span className="text-[9px] leading-none text-hce-muted">{pieza}</span>
          <span className="h-2.5 text-[8px] leading-none text-hce-clay">
            {tieneDetalle ? [entrada.recesion ? `R${entrada.recesion}` : '', entrada.movilidad ? `M${entrada.movilidad}` : ''].filter(Boolean).join(' ') : ''}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="flex w-64 flex-col gap-3">
        <p className="text-sm font-medium text-hce-ink">
          {t('formularios.f033.odontograma.pieza')} {pieza}
        </p>
        <div className="flex flex-col gap-1">
          <Label>{t('formularios.f033.odontograma.hallazgo')}</Label>
          <Select
            value={hallazgo ?? 'sano'}
            onValueChange={(v) => {
              const nuevo = v === 'sano' ? undefined : (v as CodigoHallazgoOdontograma);
              // Una pieza ausente/perdida no tiene superficies que marcar.
              onChange({ ...entrada, hallazgo: nuevo, superficies: nuevo && HALLAZGOS_DE_PERDIDA.has(nuevo) ? undefined : entrada.superficies });
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CODIGOS_HALLAZGO.filter((codigo) => !HALLAZGOS_POR_SUPERFICIE.has(codigo) || codigo === hallazgo).map((codigo) => (
                <SelectItem key={codigo} value={codigo}>
                  {t(`formularios.f033.odontograma.hallazgos.${codigo}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label>{t('formularios.f033.odontograma.superficies')}</Label>
          <div className="grid grid-cols-2 gap-2">
            {SUPERFICIES_DENTALES.map((sup) => (
              <div key={sup} className="flex flex-col gap-0.5">
                <span className="text-[11px] text-hce-muted">{t(`formularios.f033.odontograma.superficie.${sup}`)}</span>
                <Select value={entrada.superficies?.[sup] ?? 'sana'} onValueChange={(v) => onChange(conSuperficie(entrada, sup, v))}>
                  <SelectTrigger aria-label={`${t('formularios.f033.odontograma.pieza')} ${pieza} — ${t(`formularios.f033.odontograma.superficie.${sup}`)}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sana">{t('formularios.f033.odontograma.superficieSana')}</SelectItem>
                    <SelectItem value="caries">{t('formularios.f033.odontograma.hallazgos.caries')}</SelectItem>
                    <SelectItem value="obturado">{t('formularios.f033.odontograma.hallazgos.obturado')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </div>
        {!temporal && (
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <Label>{t('formularios.f033.odontograma.recesion')}</Label>
              <Input
                type="number"
                min={1}
                max={4}
                value={entrada.recesion ?? ''}
                onChange={(e) => onChange({ ...entrada, recesion: e.target.value ? Number(e.target.value) : undefined })}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label>{t('formularios.f033.odontograma.movilidad')}</Label>
              <Input
                type="number"
                min={1}
                max={3}
                value={entrada.movilidad ?? ''}
                onChange={(e) => onChange({ ...entrada, movilidad: e.target.value ? Number(e.target.value) : undefined })}
              />
            </div>
          </div>
        )}
        <Button type="button" size="sm" onClick={() => setAbierto(false)}>
          {t('formularios.f033.odontograma.listo')}
        </Button>
      </PopoverContent>
    </Popover>
  );
}

function Fila({ piezas, estado, onChange }: { piezas: readonly string[]; estado: EstadoOdontograma; onChange: (pieza: string, entrada: EntradaOdontogramaUi) => void }): JSX.Element {
  return (
    <div className="flex justify-center gap-1 sm:gap-1.5">
      {piezas.map((pieza) => (
        <Diente key={pieza} pieza={pieza} entrada={estado[pieza] ?? {}} onChange={(entrada) => onChange(pieza, entrada)} />
      ))}
    </div>
  );
}

export interface OdontogramaVisualProps {
  readonly estado: EstadoOdontograma;
  readonly onChange: (estado: EstadoOdontograma) => void;
}

export function OdontogramaVisual({ estado, onChange }: OdontogramaVisualProps): JSX.Element {
  const { t } = useTranslation();

  function actualizarPieza(pieza: string, entrada: EntradaOdontogramaUi): void {
    onChange({ ...estado, [pieza]: entrada });
  }

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-border bg-hce-surface p-4">
      <div className="overflow-x-auto pb-1">
        <div className="flex min-w-[520px] flex-col items-center gap-2.5 py-1">
          <Fila piezas={FILA_PERMANENTE_SUPERIOR} estado={estado} onChange={actualizarPieza} />
          <Fila piezas={FILA_TEMPORAL_SUPERIOR} estado={estado} onChange={actualizarPieza} />
          <div className="my-0.5 h-px w-2/3 bg-border" />
          <Fila piezas={FILA_TEMPORAL_INFERIOR} estado={estado} onChange={actualizarPieza} />
          <Fila piezas={FILA_PERMANENTE_INFERIOR} estado={estado} onChange={actualizarPieza} />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border pt-3 text-xs text-hce-muted">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: ESTILO_POR_CATEGORIA.rojo.fondo }} />
          {t('formularios.f033.odontograma.leyendaRojo')}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: ESTILO_POR_CATEGORIA.azul.fondo }} />
          {t('formularios.f033.odontograma.leyendaAzul')}
        </span>
      </div>
    </div>
  );
}
